import { motion } from "framer-motion";
import { useTranslation } from "../hooks/useTranslation";

export default function Skills() {
  const { t, language } = useTranslation();
  
  const skillCategories = {
    [t('skills.categories.mlai')]: {
      icon: "🤖",
      skills: ["Python", "PyTorch", "TensorFlow", "Keras", "Scikit-learn", "XGBoost", "NumPy", "Pandas", "Matplotlib", "Feature Engineering"],
      usedIn: ["NAI System", "TSception BCI", "Enterprise Fraud Detection", "EcoFusionAI", "NeuroSync"],
      color: "from-blue-500/10 to-cyan-500/10"
    },
    [t('skills.categories.bci')]: {
      icon: "🧠",
      skills: ["EEG Signal Processing", "P300", "Motor Imagery", "Brain-Computer Interfaces", "MNE-Python", "Signal Processing", "Neural Classification", "Deep Learning for EEG"],
      usedIn: ["NAI System (IEEE)", "TSception BCI", "Neurogenesis AI", "NeuroSync"],
      color: "from-purple-500/10 to-pink-500/10"
    },
    [t('skills.categories.genai')]: {
      icon: "✨",
      skills: ["Google Gemini", "AWS Bedrock", "Claude 3.5", "LLM APIs", "Agentic AI", "NLP", "Voice AI", "Prompt Engineering", "Multi-step Agents"],
      usedIn: ["Swasth Saathi", "SkimSage", "Udaan-AI", "EcoPulse α", "MoodLift"],
      color: "from-green-500/10 to-teal-500/10"
    },
    [t('skills.categories.software')]: {
      icon: "💻",
      skills: ["React", "TypeScript", "Node.js", "Express", "FastAPI", "REST APIs", "Git", "Clean Architecture", "MERN Stack"],
      usedIn: ["UptoSkills AI", "Enterprise Fraud Detection", "Udaan-AI", "Portfolio"],
      color: "from-orange-500/10 to-red-500/10"
    },
    [t('skills.categories.cloud')]: {
      icon: "☁️",
      skills: ["AWS Bedrock", "AWS ECS/Fargate", "AWS S3", "Google Cloud Run", "Docker", "Streamlit", "Cloud Architecture"],
      usedIn: ["Enterprise Fraud Detection", "EcoPulse α", "SkimSage", "Streamlit Apps"],
      color: "from-indigo-500/10 to-purple-500/10"
    },
    [t('skills.categories.data')]: {
      icon: "📊",
      skills: ["SQL", "MySQL", "MongoDB", "Power BI", "Excel", "Data Visualization", "Data Preprocessing"],
      usedIn: ["Future Market Insights", "Amazon Dashboard", "Student Dashboard"],
      color: "from-yellow-500/10 to-orange-500/10"
    }
  };

  const workExperience = language === 'ja' ? [
    {
      title: t('skills.experience.jobs.fmi.title'),
      company: t('skills.experience.jobs.fmi.company'),
      location: t('skills.experience.jobs.fmi.location'),
      duration: t('skills.experience.jobs.fmi.duration'),
      type: "Completed",
      achievements: t('skills.experience.jobs.fmi.achievements'),
      skills: t('skills.experience.jobs.fmi.skills')
    },
    {
      title: t('skills.experience.jobs.uptoskills.title'),
      company: t('skills.experience.jobs.uptoskills.company'),
      location: t('skills.experience.jobs.uptoskills.location'),
      duration: t('skills.experience.jobs.uptoskills.duration'),
      type: "Completed",
      achievements: t('skills.experience.jobs.uptoskills.achievements'),
      skills: t('skills.experience.jobs.uptoskills.skills')
    },
    {
      title: t('skills.experience.jobs.edunet.title'),
      company: t('skills.experience.jobs.edunet.company'),
      location: t('skills.experience.jobs.edunet.location'),
      duration: t('skills.experience.jobs.edunet.duration'),
      type: "Completed",
      achievements: t('skills.experience.jobs.edunet.achievements'),
      skills: t('skills.experience.jobs.edunet.skills')
    }
  ] : [
    {
      title: "Data Analyst Intern",
      company: "Future Market Insights (FMI)",
      location: "Pune – India",
      duration: "Mar 2026 – Jun 2026 (3 months)",
      type: "Completed",
      achievements: [
        "Assisted in data cleaning, validation, and preparation of market datasets",
        "Worked with Advanced Excel (Pivot Tables, VLOOKUP/XLOOKUP) for analysis and reporting",
        "Supported dashboard creation and data visualization using Power BI",
        "Contributed to basic market analysis and report structuring",
        "Explored AI tools to improve data processing workflows"
      ],
      skills: ["Advanced Excel", "Power BI", "Data Analysis", "AI Tools", "Market Research"]
    },
    {
      title: "Machine Learning Intern",
      company: "UpToSkills",
      location: "Remote – India",
      duration: "Jan 2025 – Apr 2025 (3 months)",
      type: "Completed",
      achievements: [
        "Built full-stack MERN AI Resume Builder with Gemini API integration for ATS-friendly resume generation",
        "Collaborated with 10-member development team (6 frontend, 4 backend) during 3-month internship",
        "Led team for 2 weeks during critical sprint phase, coordinating frontend/backend integration",
        "Integrated AI backend with frontend, implementing multiple professional resume templates",
        "Contributed to application with AI-powered resume generation workflow (project ongoing at internship end)"
      ],
      skills: ["MERN Stack", "Gemini API", "Team Leadership", "AI Integration", "ATS Templates", "Project Management"]
    },
    {
      title: "Artificial Intelligence Intern",
      company: "Edunet Foundation",
      location: "Remote – India",
      duration: "Nov 2024 – Dec 2024 (1 month)",
      type: "Completed",
      achievements: [
        "Developed ML pipelines with data preprocessing, feature engineering, and model training using Scikit-learn and TensorFlow",
        "Optimized model accuracy by 10–15% through hyperparameter tuning and iterative experimentation",
        "Delivered working AI prototypes demonstrating applied ML for real-world use cases",
        "Collaborated in a small R&D team to apply ML to real-world education datasets"
      ],
      skills: ["TensorFlow", "Scikit-learn", "Data Preprocessing", "Model Optimization", "R&D"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-secondary to-primary">
      <div className="section-padding">
        <div className="container-width">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('skills.title')}
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-accent to-accent-light mx-auto mb-8"></div>
            <p className="text-xl text-muted-light max-w-3xl mx-auto">
              {t('skills.subtitle')}
            </p>
          </motion.div>

          {/* Technical Skills - No metrics */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-accent mb-12 text-center">
              {t('skills.title').split('&')[0].trim()}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(skillCategories).map(([category, data], index) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * index, duration: 0.6 }}
                  className={`card card-hover bg-gradient-to-br ${data.color}`}
                >
                  <div className="flex items-center mb-4">
                    <span className="text-3xl mr-4">{data.icon}</span>
                    <h3 className="text-lg font-bold text-accent">{category}</h3>
                  </div>

                  {/* Skills List */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {data.skills.map((skill) => (
                      <motion.span
                        key={skill}
                        whileHover={{ scale: 1.05 }}
                        className="bg-tertiary/30 text-muted-light px-3 py-2 rounded-lg text-sm border border-tertiary/20 hover:border-accent/30 hover:text-accent transition-all duration-200"
                      >
                        {skill}
                      </motion.span>
                    ))}
                  </div>

                  {/* Used In Projects */}
                  <div className="pt-3 border-t border-tertiary/20">
                    <div className="text-xs text-accent font-semibold mb-2">{t('skills.usedIn')}</div>
                    <div className="flex flex-wrap gap-1">
                      {data.usedIn.map((project) => (
                        <span key={project} className="text-xs bg-accent/10 text-accent px-2 py-1 rounded-full">
                          {project}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Work Experience */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-accent mb-12 text-center">{t('skills.experience.title')}</h2>
            <div className="space-y-8 max-w-4xl mx-auto">
              {workExperience.map((job, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 * index, duration: 0.6 }}
                  className="card card-hover"
                >
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{job.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          job.type === 'Current'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        }`}>
                          {job.type === 'Current' ? t('skills.experience.current') : t('skills.experience.completed')}
                        </span>
                      </div>
                      <p className="text-accent font-semibold text-lg">{job.company}</p>
                      <p className="text-sm text-muted">{job.location} • {job.duration}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-accent mb-3">{t('skills.experience.achievements')}</h4>
                    <ul className="space-y-2">
                      {(job.achievements as string[]).map((achievement: string, i: number) => (
                        <li key={i} className="text-sm text-muted-light flex items-start">
                          <span className="text-accent mr-3 mt-1 text-xs">●</span>
                          <span>{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-accent mb-3">{t('skills.experience.technologies')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {(job.skills as string[]).map((skill: string) => (
                        <span key={skill} className="text-xs bg-accent/10 text-accent px-3 py-1 rounded-full border border-accent/20">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Summary */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="card bg-gradient-to-r from-accent/10 to-accent-light/10 text-center max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-accent mb-4">{t('skills.summary.title')}</h3>
            <p className="text-lg text-muted-light mb-6">
              {t('skills.summary.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn-primary">
                {t('skills.summary.cta.collaborate')}
              </a>
              <a href="/projects" className="btn-secondary">
                {t('skills.summary.cta.viewWork')}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}